import { useCallback, useRef } from 'react';

/**
 * useKanbanScroll Hook
 * 
 * Handles horizontal scrolling for the Kanban board:
 * - Mouse wheel (deltaY only): converts to horizontal scroll when not over column
 * - Trackpad horizontal swipe (deltaX): always scrolls board horizontally
 * - Trackpad vertical swipe (deltaY): scrolls column vertically if over column
 * - Smooth animation using requestAnimationFrame
 * 
 * @param {Object} options
 * @param {React.RefObject} options.boardRef - Ref to the board container element
 * @param {number} [options.scrollSpeed=1.5] - Multiplier for scroll speed
 * @param {number} [options.easing=0.15] - Easing factor for smooth scroll (0-1, higher = faster)
 * @returns {Object} - { handleBoardWheel, handleViewWheel }
 */
const useKanbanScroll = ({ boardRef, scrollSpeed = 1.5, easing = 0.15 }) => {
  // Accumulated scroll delta for smooth animation
  const scrollTargetRef = useRef(null);
  const scrollAnimationRef = useRef(null);

  // Smooth scroll animation using requestAnimationFrame
  const animateScroll = useCallback(() => {
    const board = boardRef.current;
    if (!board || scrollTargetRef.current === null) {
      scrollAnimationRef.current = null;
      return;
    }

    const current = board.scrollLeft;
    const target = scrollTargetRef.current;
    const diff = target - current;

    // If we're close enough, snap to target and stop
    if (Math.abs(diff) < 1) {
      board.scrollLeft = target;
      scrollTargetRef.current = null;
      scrollAnimationRef.current = null;
      return;
    }

    // Ease towards target
    board.scrollLeft = current + diff * easing;
    scrollAnimationRef.current = requestAnimationFrame(animateScroll);
  }, [boardRef, easing]);

  // Start or update smooth scroll to target
  const smoothScrollTo = useCallback((delta) => {
    const board = boardRef.current;
    if (!board) return;

    // Clamp target within scroll bounds
    const maxScroll = board.scrollWidth - board.clientWidth;
    const currentTarget = scrollTargetRef.current ?? board.scrollLeft;
    scrollTargetRef.current = Math.max(0, Math.min(maxScroll, currentTarget + delta));
    
    // Start animation if not already running
    if (!scrollAnimationRef.current) {
      scrollAnimationRef.current = requestAnimationFrame(animateScroll);
    }
  }, [boardRef, animateScroll]);

  // Handle wheel events on the board
  // - Mouse wheel (deltaY only): convert to horizontal scroll when not over column
  // - Trackpad horizontal swipe (deltaX): always scroll board horizontally
  // - Trackpad vertical swipe (deltaY): scroll column vertically if over column
  const handleBoardWheel = useCallback((e) => {
    const board = boardRef.current;
    if (!board) return;
    
    const canScrollHorizontally = board.scrollWidth > board.clientWidth;
    const isOverColumn = e.target.closest('.kanban-column');
    
    // Trackpad horizontal swipe (deltaX) - always scroll board horizontally
    if (e.deltaX !== 0 && canScrollHorizontally) {
      e.preventDefault();
      smoothScrollTo(e.deltaX * scrollSpeed);
      return;
    }

    // If over a column, let vertical scroll happen naturally (for column cards)
    if (isOverColumn) {
      return; // Let default vertical scroll behavior happen
    }

    // Mouse wheel or trackpad vertical (not over column) - convert to horizontal
    if (e.deltaY !== 0 && canScrollHorizontally) {
      e.preventDefault();
      smoothScrollTo(e.deltaY * scrollSpeed);
    }
  }, [boardRef, scrollSpeed, smoothScrollTo]);

  // Handle wheel on view/header areas - also scroll board horizontally
  const handleViewWheel = useCallback((e) => {
    const board = boardRef.current;
    if (!board) return;

    // Don't handle if already handled by board (over columns, etc.)
    const isOverBoard = e.target.closest('.kanban-board');
    if (isOverBoard) return;

    const canScrollHorizontally = board.scrollWidth > board.clientWidth;
    if (!canScrollHorizontally) return;

    // Trackpad horizontal swipe
    if (e.deltaX !== 0) {
      e.preventDefault();
      smoothScrollTo(e.deltaX * scrollSpeed);
      return;
    }
    
    // Mouse wheel or trackpad vertical
    if (e.deltaY !== 0) {
      e.preventDefault();
      smoothScrollTo(e.deltaY * scrollSpeed);
    }
  }, [boardRef, scrollSpeed, smoothScrollTo]);

  return {
    handleBoardWheel,
    handleViewWheel,
  };
};

export default useKanbanScroll;

